import { Component } from '@theme/component';

/**
 * "Notify Me When Available" button for coming-soon products.
 *
 * Captures an email and creates a Klaviyo Back in Stock subscription for the
 * currently selected variant via Klaviyo's public client-side endpoint:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=PUBLIC_KEY
 *
 * The Klaviyo public key (company_id) is auto-detected from the onsite
 * `klaviyo.js` script injected by the Klaviyo app embed, or supplied via the
 * `data-company-id` attribute (block setting). The variant is referenced using
 * Klaviyo's Shopify catalog composite id: `$shopify:::$default:::<variantId>`.
 *
 * Docs: https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} [trigger]
 * @property {HTMLFormElement} [form]
 * @property {HTMLInputElement} [email]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [message]
 *
 * @extends {Component<Refs>}
 */
class NotifyMeButton extends Component {
  /** Klaviyo client API revision (https://developers.klaviyo.com). */
  static REVISION = '2024-06-15';

  connectedCallback() {
    super.connectedCallback();
    // Keep the target variant in sync if the product has a variant picker.
    document.addEventListener('variant:update', this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  /**
   * Updates the target variant when a variant picker selection changes.
   * Arrow field so it is auto-bound and stable for add/removeEventListener.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const id = event.detail?.resource?.id;
    if (id) this.dataset.variantId = String(id);
  };

  /**
   * Reveals the email form and moves focus into it.
   * @param {MouseEvent} _event
   */
  open(_event) {
    const { trigger, form, email } = this.refs;
    if (!form) return;
    form.hidden = false;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      trigger.hidden = true;
    }
    email?.focus();
  }

  /**
   * Submits the back-in-stock subscription to Klaviyo.
   * @param {SubmitEvent} event
   */
  async subscribe(event) {
    event.preventDefault();

    const { email, submit, message } = this.refs;
    if (!email || !(email instanceof HTMLInputElement)) return;
    if (!email.checkValidity()) {
      email.reportValidity();
      return;
    }

    const companyId = this.#companyId();
    const variantId = this.dataset.variantId;

    if (!companyId) {
      this.#setMessage(message, this.dataset.errorText || 'Something went wrong. Please try again later.', 'error');
      console.error('[notify-me-button] No Klaviyo company_id found (onsite script or data-company-id).');
      return;
    }
    if (!variantId) {
      this.#setMessage(message, this.dataset.errorText || 'Something went wrong. Please try again later.', 'error');
      console.error('[notify-me-button] Missing data-variant-id.');
      return;
    }

    this.#busy(submit, true);
    this.#setMessage(message, '', null);

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: NotifyMeButton.REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: email.value.trim() },
                  },
                },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${variantId}`,
                  },
                },
              },
            },
          }),
        }
      );

      if (response.status === 202 || response.ok) {
        const { form } = this.refs;
        if (form) form.hidden = true;
        this.#setMessage(
          message,
          this.dataset.successText || "You're on the list! We'll email you when it's back.",
          'success'
        );
      } else {
        const detail = await response.text().catch(() => '');
        console.error('[notify-me-button] Klaviyo responded', response.status, detail);
        this.#setMessage(message, this.dataset.errorText || 'Something went wrong. Please try again later.', 'error');
        this.#busy(submit, false);
      }
    } catch (error) {
      console.error('[notify-me-button]', error);
      this.#setMessage(message, this.dataset.errorText || 'Something went wrong. Please try again later.', 'error');
      this.#busy(submit, false);
    }
  }

  /**
   * Resolves the Klaviyo public key from the data attribute or the onsite script.
   * @returns {string} The company_id, or '' if not found.
   */
  #companyId() {
    if (this.dataset.companyId) return this.dataset.companyId;

    const script = document.querySelector('script[src*="klaviyo.js"]');
    const src = script?.getAttribute('src') || '';
    if (!src) return '';

    // Form 1: .../klaviyo.js?company_id=XXXX
    const queryMatch = src.match(/[?&]company_id=([^&]+)/);
    if (queryMatch) return decodeURIComponent(queryMatch[1]);

    // Form 2: .../onsite/js/XXXX/klaviyo.js
    const pathMatch = src.match(/onsite\/js\/([^/]+)\/klaviyo\.js/);
    if (pathMatch) return pathMatch[1];

    return '';
  }

  /**
   * @param {HTMLButtonElement | undefined} submit
   * @param {boolean} busy
   */
  #busy(submit, busy) {
    if (!submit) return;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /**
   * @param {HTMLElement | undefined} message
   * @param {string} text
   * @param {'success' | 'error' | null} state
   */
  #setMessage(message, text, state) {
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('notify-me-form__message--success', state === 'success');
    message.classList.toggle('notify-me-form__message--error', state === 'error');
  }
}

if (!customElements.get('notify-me-button')) {
  customElements.define('notify-me-button', NotifyMeButton);
}
